import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  ListCarriersResponse,
  CreateCarrierRequest,
  UpdateCarrierRequest,
  CarrierContact,
  CarrierContactType,
} from './types';

// List all carriers
export function useListCarriers() {
  return useQuery<ListCarriersResponse>({
    queryKey: ['carriers'],
    queryFn: async () => {
      const { data } = await api.get('/organization/carriers');
      return data;
    },
  });
}

// ── Carrier contact CRUD ──────────────────────────────────────────────────────

interface ContactPayload {
  type: CarrierContactType;
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
}

export function useCreateCarrierContact(carrierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ContactPayload) => {
      const { data } = await api.post(`/organization/carriers/${carrierId}/contacts`, payload);
      return data.contact as CarrierContact;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carriers'] }),
  });
}

export function useUpdateCarrierContact(carrierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactId, ...payload }: ContactPayload & { contactId: string }) => {
      const { data } = await api.put(
        `/organization/carriers/${carrierId}/contacts/${contactId}`,
        payload
      );
      return data.contact as CarrierContact;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carriers'] }),
  });
}

export function useDeleteCarrierContact(carrierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      await api.delete(`/organization/carriers/${carrierId}/contacts/${contactId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carriers'] }),
  });
}

// ── Carrier CRUD ──────────────────────────────────────────────────────────────

// Create carrier
export function useCreateCarrier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateCarrierRequest) => {
      const { data } = await api.post('/organization/carriers', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Update carrier
export function useUpdateCarrier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ carrierId, ...payload }: UpdateCarrierRequest & { carrierId: string }) => {
      const { data } = await api.put(`/organization/carriers/${carrierId}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Delete carrier
export function useDeleteCarrier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (carrierId: string) => {
      const { data } = await api.delete(`/organization/carriers/${carrierId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Assign carrier to order item
export function useAssignCarrier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, carrierId }: { itemId: string; carrierId: string }) => {
      const { data } = await api.post(
        `/organization/carriers/order-items/${itemId}/assign-carrier`,
        { carrierId }
      );
      return data;
    },
    onSuccess: () => {
      // The table, and the manage-load dialog's own query — that one is frozen
      // while open, so nothing else would refresh it.
      queryClient.invalidateQueries({ queryKey: ['org:orders:items'] });
      queryClient.invalidateQueries({ queryKey: ['org:orders:item'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Remove carrier from order item
export function useRemoveCarrier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const { data } = await api.delete(
        `/organization/carriers/order-items/${itemId}/remove-carrier`
      );
      return data;
    },
    onSuccess: () => {
      // The table, and the manage-load dialog's own query — that one is frozen
      // while open, so nothing else would refresh it.
      queryClient.invalidateQueries({ queryKey: ['org:orders:items'] });
      queryClient.invalidateQueries({ queryKey: ['org:orders:item'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Download BOL PDF
export function useGenerateBOL() {
  return useMutation({
    mutationFn: async ({ itemId, loadNumber }: { itemId: string; loadNumber?: string | null }) => {
      const response = await api.get(`/organization/carriers/order-items/${itemId}/generate-bol`, {
        responseType: 'blob',
      });

      const filename = loadNumber ? `BOL-${loadNumber}.pdf` : `BOL-${itemId.slice(-6)}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response.data;
    },
  });
}

// Preview BOL PDF in a modal dialog
export function usePreviewBOL() {
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await api.get(`/organization/carriers/order-items/${itemId}/generate-bol`, {
        params: { disposition: 'inline' },
        responseType: 'blob',
      });
      return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    },
  });
}

/**
 * Outcome of a BOL send. Each recipient gets their own email, so some can land
 * while others fail — callers need to report both, not just success or error.
 */
export interface SendBOLResult {
  message: string;
  sent: string[];
  failed: Array<{ email: string; error: string }>;
}

// Send BOL via email
export function useSendBOL() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      emails,
      note,
      contactName,
    }: {
      itemId: string;
      /** One separate email is sent per address. */
      emails: string[];
      note?: string;
      contactName?: string;
    }): Promise<SendBOLResult> => {
      const { data } = await api.post(`/organization/carriers/order-items/${itemId}/send-bol`, {
        emails,
        note,
        contactName,
      });
      return data;
    },
    onSuccess: () => {
      // The table, and the manage-load dialog's own query — frozen while open,
      // so without the singular key the "BOL sent" badge waits for a reload.
      queryClient.invalidateQueries({ queryKey: ['org:orders:items'] });
      queryClient.invalidateQueries({ queryKey: ['org:orders:item'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

// Plain (non-hook) BOL send for fan-out callers like the bulk Send BOL action.
export async function sendBolDirect(itemId: string, emails: string[]): Promise<SendBOLResult> {
  const { data } = await api.post(`/organization/carriers/order-items/${itemId}/send-bol`, {
    emails,
  });
  return data;
}

export async function assignCarrierDirect(itemId: string, carrierId: string) {
  const { data } = await api.post(`/organization/carriers/order-items/${itemId}/assign-carrier`, {
    carrierId,
  });
  return data;
}

export async function removeCarrierDirect(itemId: string) {
  const { data } = await api.delete(`/organization/carriers/order-items/${itemId}/remove-carrier`);
  return data;
}
